import type { ButtonProps } from '@mui/material/Button';

import { createTheme, type BaseTheme } from '../theme';

const theme = createTheme({
  components: {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    //@ts-ignore
    MuiLoadingButton: {
      defaultProps: {
        variant: 'contained',
        loadingPosition: 'start',
      },
      styleOverrides: {
        loadingIndicatorStart: {
          position: 'initial',
          marginRight: '4px',
        },
        root: ({ theme, ownerState }: { theme: BaseTheme; ownerState: ButtonProps }) => ({
          '&.Mui-disabled': {
            backgroundColor:
              ownerState?.variant === 'contained'
                ? theme.palette.grey[100]
                : theme.palette.common.white,
            borderColor: ownerState?.variant === 'contained' ? undefined : theme.palette.grey[400],
            color: theme.palette.text.disabled,
          },
        }),
      },
    },

    MuiButton: {
      defaultProps: {
        variant: 'contained',
      },
      styleOverrides: {
        root: {
          minWidth: '100px',
          borderRadius: '4px',
          boxShadow: 'none',
          ':hover': {
            boxShadow: 'none',
          },
        },
        outlined: ({ theme }) => ({
          backgroundColor: theme.palette.common.white,
        }),
        outlinedSizeSmall: ({ theme }) => ({
          padding: theme.spacing(1.25, 2.75),
        }),
        outlinedSizeMedium: ({ theme }) => ({
          padding: theme.spacing(1.75, 3.25),
        }),
        outlinedSizeLarge: ({ theme }) => ({
          padding: theme.spacing(2.25, 3.75),
        }),
        sizeSmall: ({ theme: { unstable_sx: sx, ...theme } }) =>
          sx({
            ...theme.typography.body2Semibold,
            py: 0.75,
            px: 1.5,
            '.MuiButton-startIcon': { mr: 0.75, ml: 0 },
            '.MuiButton-endIcon': { ml: 0.75, mr: 0 },
          }),
        sizeMedium: ({ theme: { unstable_sx: sx, ...theme } }) =>
          sx({
            ...theme.typography.body2Semibold,
            py: 1,
            px: 1.75,
            '.MuiButton-startIcon': { mr: 1, ml: 0 },
            '.MuiButton-endIcon': { ml: 1, mr: 0 },
          }),
        sizeLarge: ({ theme: { unstable_sx: sx, ...theme } }) =>
          sx({
            ...theme.typography.body1Semibold,
            py: 1.25,
            px: 2,
            '.MuiButton-startIcon': { mr: 1, ml: 0 },
            '.MuiButton-endIcon': { ml: 1, mr: 0 },
          }),
        iconSizeSmall: { '.MuiSvgIcon-root': { fontSize: 20 } },
        iconSizeMedium: { '.MuiSvgIcon-root': { fontSize: 20 } },
        iconSizeLarge: { '.MuiSvgIcon-root': { fontSize: 24 } },
      },
      variants: [
        {
          props: { color: 'primary' },
          style: ({ theme }) => ({
            color: theme.palette.text.primary,
          }),
        },
        {
          props: { color: 'secondary' },
          style: ({ theme }) => ({
            color: theme.palette.text.primary,
          }),
        },
        {
          props: { color: 'primary', variant: 'outlined' },
          style: ({ theme: { unstable_sx: sx, ...theme } }) =>
            sx({
              bgcolor: theme.palette.common.white,
              ':hover': {
                bgcolor: theme.palette.primary.dark,
              },
            }),
        },
        {
          props: { color: 'secondary', variant: 'contained' },
          style: ({ theme: { unstable_sx: sx, ...theme } }) =>
            sx({
              bgcolor: theme.palette.common.white,
              ':hover': {
                bgcolor: theme.palette.secondary.lighter,
              },
            }),
        },
        {
          props: { color: 'secondary', variant: 'outlined' },
          style: ({ theme: { unstable_sx: sx, ...theme } }) =>
            sx({
              bgcolor: theme.palette.common.white,
              borderColor: theme.palette.secondary.main,
              ':hover': {
                bgcolor: theme.palette.secondary.lighter,
              },
            }),
        },
        {
          props: { color: 'error', variant: 'contained' },
          style: ({ theme: { unstable_sx: sx, ...theme } }) =>
            sx({
              borderColor: theme.palette.error.main,
              ':hover': {
                bgcolor: theme.palette.error.dark,
              },
            }),
        },
      ],
    },
  },
});

export default theme.components;
